import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { Button, Card, CardBody, CardHeader, Divider, Spinner, TableCell, TableRow } from "@nextui-org/react";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";

import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import RolesAccess from "../../../../enums/roles";
import StatusPengajuanPelaporan from "../../../../enums/statusPengajuanPelaporan";
import ModalFinalizeReport from "../../../../components/fragments/admin/report/preview/ModalFinalizeReport";
import ModalReportReview from "../../../../components/fragments/admin/report/preview/ModalReportReview";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import ClientTablePagination from "../../../../components/elements/ClientTablePagination";
import useAuth from "../../../../hooks/useAuth";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import { formattedDate, hasValidRole, hasValidStatus } from "../../../../services/helpers";
import { getFetcher, getPdfUrl } from "../../../../services/api";
import useListCompany from "../../../../hooks/useListCompany";
import { useParams } from "react-router-dom";
import RootAdmin from "../../../../components/layouts/RootAdmin";

export default function TableReportReview({ isPreviewOnly = false }) {
  const { companyId, periodId } = useParams();
  const { roles } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // State untuk loading button
  const [isLoadingBuktiLaporan, setIsLoadingBuktiLaporan] = useState(false);
  const [isLoadingTandaTerima, setIsLoadingTandaTerima] = useState(false);

  const { data, isLoading, mutate } = useSWR(companyId && periodId ? `/api/period/preview-report?companyId=${companyId}&periodId=${periodId}` : null, getFetcher);
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR(`/api/period/all`, getFetcher);

  useEffect(() => {
    if (dataPeriod?.length > 0) {
      const activePeriod = dataPeriod?.find((item) => item?.id === periodId);
      if (activePeriod) {
        setSelectedPeriod({ label: activePeriod.name, value: activePeriod.id });
      }
    }
    if (dataCompany?.data?.length > 0) {
      const company = dataCompany?.data?.find((item) => item?.id === companyId);
      if (company) {
        setSelectedCompany({ label: company.name, value: company.id });
      }
    }
  }, [dataPeriod, dataCompany]);

  // Fungsi format angka pintar
  const formatNumber = (num) => {
      // 1. Cek apakah num kosong atau bukan angka, kembalikan "0"
      if (num === null || num === undefined || isNaN(num)) return "0";

      // 2. Format menggunakan standar angka Indonesia (id-ID)
      return new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0, // Jangan paksa ada angka di belakang koma jika angkanya bulat
      maximumFractionDigits: 2  // Maksimal 2 angka di belakang koma jika angkanya desimal
      }).format(num);
  };
  const header = ["Jenis B3", "Stok Awal", "Pembelian Lokal", "Pembelian Impor", "Penggunaan", "Distribusi", "Produksi", "Stok Akhir"];
  const content = (item) => (
    <TableRow key={item?.id ?? uuidv4()}>
      <TableCell>
          {item.dataBahanB3.casNumber} - {item.dataBahanB3.namaBahanKimia}
      </TableCell>
      <TableCell className="text-ink-500 text-right">{formatNumber(item.stokAwal)} Kg</TableCell>
      <TableCell className="text-success text-right">+ {formatNumber(item.pembelianLokal)} Kg</TableCell>
      <TableCell className="text-success text-right">+ {formatNumber(item.pembelianImport)} Kg</TableCell>
      <TableCell className="text-danger text-right">- {formatNumber(item.penggunaan)} Kg</TableCell>
      <TableCell className="text-danger text-right">- {formatNumber(item.distribusi)} Kg</TableCell>
      <TableCell className="text-success text-right">+ {formatNumber(item.produksi)} Kg</TableCell>
      <TableCell className="text-ink-500 text-right">{formatNumber(item.stokAkhir)} Kg</TableCell>
    </TableRow>
  );

  async function openPdfDirectly(endpoint, type = "bukti") {
    try {
      // Set loading state berdasarkan tipe button
      if (type === "bukti") {
        setIsLoadingBuktiLaporan(true);
      } else if (type === "tanda") {
        setIsLoadingTandaTerima(true);
      }

      await getPdfUrl(endpoint);
    } catch (error) {
      toast.error("Failed to open PDF");
    } finally {
      // Reset loading state
      if (type === "bukti") {
        setIsLoadingBuktiLaporan(false);
      } else if (type === "tanda") {
        setIsLoadingTandaTerima(false);
      }
    }
  }

  return (
    <RootAdmin>
      <>
        {isLoading || isLoadingCompany || selectedCompany === null || selectedPeriod === null || isLoadingPeriod ? (
          <div className="flex items-center justify-center mt-3"></div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PENGELOLA]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, null, undefined]) && (
              <ModalFinalizeReport endpoint="/api/period/finalize-pelaporan" mutate={mutate} selectedCompany={selectedCompany} selectedPeriod={selectedPeriod} setSelectedCompany={setSelectedCompany} setSelectedPeriod={setSelectedPeriod} />
            )}
            {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_PELAPORAN]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN]) && (
              <ModalReportReview endpoint="/api/period/review-and-approve" mutate={mutate} selectedCompany={selectedCompany} selectedPeriod={selectedPeriod} setSelectedCompany={setSelectedCompany} setSelectedPeriod={setSelectedPeriod} />
            )}
          </div>
        )}
        {isLoading || isLoadingCompany || selectedCompany === null || selectedPeriod === null || isLoadingPeriod ? (
          <div className="flex items-center justify-center mt-3">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3 mt-3">
            <div className="col-span-1 flex flex-col gap-3">
              <Card radius="sm" className="col-span-1">
                <CardHeader>Perusahaan</CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Nama Perusahaan</span>
                    <span className="text-sm font-medium">{data?.company?.name || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Alamat</span>
                    <span className="text-sm font-medium">{data?.company?.alamatKantor || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Tipe Perusahaan</span>
                    <span className="text-sm font-medium">{data?.company?.tipePerusahaan?.map((item) => item?.name).join(", ") || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Email Perusahaan</span>
                    <span className="text-sm font-medium">{data?.company?.emailKantor || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Fax Perusahaan</span>
                    <span className="text-sm font-medium">{data?.company?.faxKantor || "-"}</span>
                  </div>
                </CardBody>
              </Card>
              <Card radius="sm" className="col-span-1">
                <CardHeader>Periode</CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Periode</span>
                    <span className="text-sm font-medium">{data?.period?.name || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Tanggal Mulai Periode</span>
                    <span className="text-sm font-medium">{data?.period?.startPeriodDate ? formattedDate(data?.period?.startPeriodDate) : "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Tanggal Akhir Periode</span>
                    <span className="text-sm font-medium">{data?.period?.endPeriodDate ? formattedDate(data?.period?.endPeriodDate) : "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-ink-400 uppercase">Tanggal Akhir Finalisasi</span>
                    <span className="text-sm font-medium">{data?.period?.finalizationDeadline ? formattedDate(data?.period?.finalizationDeadline) : "-"}</span>
                  </div>
                </CardBody>
              </Card>
            </div>
            <Card radius="sm" className="col-span-3">
              <CardHeader>Review Laporan</CardHeader>
              <Divider />
              <CardBody>
                <div className="w-full flex justify-end items-center gap-2 mb-3">
                  <div className="w-2/4 flex flex-col justify-end">
                    <div className="flex items-center gap-2">
                      {companyId && periodId && hasValidStatus(data?.preview?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN, null, undefined]) ? (
                        <Button
                          size="sm"
                          color="primary"
                          isLoading={isLoadingBuktiLaporan}
                          isDisabled={isLoadingBuktiLaporan}
                          onPress={() => openPdfDirectly(`/api/pdf/generateBuktiLapor/${companyId}/${periodId}`, "bukti")}
                          startContent={!isLoadingBuktiLaporan && <ArrowDownTrayIcon className="size-4" />}
                        >
                          Bukti Laporan
                        </Button>
                      ) : (
                        ""
                      )}
                      {companyId && periodId && data?.preview?.statusPelaporan === StatusPengajuanPelaporan.DISETUJUI ? (
                        <Button
                          size="sm"
                          color="primary"
                          isLoading={isLoadingTandaTerima}
                          isDisabled={isLoadingTandaTerima}
                          startContent={!isLoadingTandaTerima && <ArrowDownTrayIcon className="size-4" />}
                          onPress={() => openPdfDirectly(`/api/pdf/generateTandaTerima/${companyId}/${periodId}`, "tanda")}
                        >
                          Tanda Terima
                        </Button>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
                <ClientTablePagination header={header} content={content} isLoading={isLoading} data={data?.preview} />
              </CardBody>
            </Card>
          </div>
        )}
      </>
    </RootAdmin>
  );
}
